import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { AppHeader } from '@/components/layout/AppHeader'
import { Button } from '@/components/ui/Button'
import { ResumeScanModal } from '@/features/profile/ResumeScanModal'
import { cn, formatBytes } from '@/lib/utils'
import { ONBOARDING_KEY } from './useWizard'

const ACCEPT = '.pdf,.doc,.docx,.txt'
const MAX_BYTES = 10 * 1024 * 1024

interface Props {
  onDone: () => void
}

/**
 * Shown once, before the wizard, when the account has no resume.
 *
 * The six steps are written on the promise that a resume is parsing in the
 * background; without this the footnote would be describing work that never
 * happens, and every screening question would fall back to "needs you".
 */
export function ResumeUploadGate({ onDone }: Props) {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [rejected, setRejected] = useState<string | null>(null)
  const [pending, setPending] = useState<File | null>(null)

  const accept = (file: File | undefined) => {
    if (!file) return
    setRejected(null)
    if (file.size > MAX_BYTES) {
      setRejected(`That file is ${formatBytes(file.size)}. The limit is 10 MB.`)
      return
    }
    setPending(file)
  }

  const finish = async () => {
    // Wait for the refreshed onboarding state before handing over: the
    // wizard's footnote reads resumeStatus, and rendering it against the
    // stale value would flash "not uploaded" straight after a successful
    // parse.
    await queryClient.refetchQueries({ queryKey: ONBOARDING_KEY })
    onDone()
  }

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <AppHeader />
      <div className="mx-auto w-full max-w-[640px] flex-1 px-4 pb-10 pt-6 sm:px-6 sm:pt-10">
        <main className="animate-fade-in md:card md:px-10 md:py-9">
          <p className="label-eyebrow">Resume</p>
          <h1 className="mt-2.5 text-balance text-[24px] font-semibold leading-[1.15] tracking-[-0.02em] text-ink sm:text-[30px]">
            Start with your resume
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-ink-muted sm:text-[16px]">
            We read your work history and skills from it, then use them to answer the questions
            employers ask on every application.
          </p>

          <div
            onDragOver={(event) => {
              event.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault()
              setDragging(false)
              accept(event.dataTransfer.files?.[0])
            }}
            className={cn(
              'mt-7 rounded-card border-2 border-dashed px-5 py-8 text-center transition-colors sm:mt-9 sm:py-10',
              dragging
                ? 'border-brand bg-brand-soft'
                : 'border-line-strong/70 bg-surface hover:border-line-strong md:bg-surface-muted/50',
            )}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="sr-only"
              onChange={(event) => {
                accept(event.target.files?.[0])
                event.target.value = ''
              }}
            />

            <UploadIcon />
            <p className="mt-4 text-[15px] text-ink sm:text-[16px]">
              <span className="hidden sm:inline">Drop your resume here, or </span>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="inline-flex min-h-[44px] items-center font-medium text-ink underline underline-offset-4 transition-colors hover:text-brand sm:min-h-0"
              >
                <span className="sm:hidden">Choose a file</span>
                <span className="hidden sm:inline">choose a file</span>
              </button>
            </p>
            <p className="mt-1 text-[13px] text-ink-muted">PDF, Word or plain text, up to 10 MB.</p>
          </div>

          {rejected && (
            <div
              role="alert"
              className="mt-4 rounded-field border border-danger/25 bg-danger/[0.07] px-4 py-3 text-[14px] text-danger"
            >
              {rejected}
            </div>
          )}

          <div className="mt-7 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <Button size="lg" className="w-full sm:w-auto" onClick={() => inputRef.current?.click()}>
              Upload resume
            </Button>
            <Button variant="ghost" size="lg" className="w-full sm:w-auto" onClick={onDone}>
              I&apos;ll do this later
            </Button>
          </div>

          <p className="mt-7 flex items-start gap-2 border-t border-line pt-5 text-[13px] leading-relaxed text-ink-muted">
            <LockIcon />
            Stored privately and only used to fill applications you approve.
          </p>
        </main>
      </div>

      <ResumeScanModal
        file={pending}
        onClose={() => setPending(null)}
        onDone={() => void finish()}
        // Parsing failed, but the file is stored. Let them continue rather
        // than trapping setup behind a parser we do not fully control.
        onFailed={(message) => setRejected(`${message} You can continue and fix it later.`)}
      />
    </div>
  )
}

function UploadIcon() {
  return (
    <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-brand-soft text-brand">
      <svg className="h-6 w-6" viewBox="0 0 40 40" fill="none" aria-hidden="true">
        <path
          d="M20 27V11m0 0-6 6m6-6 6 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M8 26v3a3 3 0 0 0 3 3h18a3 3 0 0 0 3-3v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  )
}

function LockIcon() {
  return (
    <svg className="mt-[3px] h-3.5 w-3.5 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  )
}
